import { FunctionComponent, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { faCheck } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { classNames } from "@/utils/classNames";
import { useIsFeatureActive } from "@/utils/useIsFeatureActive";
import { FeaturesCodes } from "@/pages/Private/pages/Users/enum/codes";

import { IssuePanel } from "./IssuePanel";
import { issuesForStep, ScoreIssue, stepStatus, StepStatus } from "./scoreIssues";
import { ScoreStep, scoreStepNumber, scoreStepPath, visibleScoreSteps } from "./scoreSteps";
import { useScoreConfig, useScoreIssues } from "./useScoreConfig";

interface ScoreTabsProps {
	currentStep: ScoreStep;
}

const PANEL_CLOSE_DELAY_MS = 180;

const Badge: FunctionComponent<{ number: number; status: StepStatus; isCurrent: boolean }> = ({
	number,
	status,
	isCurrent,
}) => {
	if (!isCurrent && status === "ok") {
		return (
			<span
				className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-status-success text-white"
				data-testid="score-tab-ok"
			>
				<Icon className="h-2.5 w-2.5" icon={faCheck} />
			</span>
		);
	}

	if (!isCurrent && status === "warn") {
		return (
			<span
				className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-status-error text-[10px] font-semibold text-white"
				data-testid="score-tab-warn"
			>
				!
			</span>
		);
	}

	return (
		<span
			className={classNames(
				"flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold",
				isCurrent ? "bg-brand-light text-white" : "bg-background-light text-text-meta"
			)}
		>
			{number}
		</span>
	);
};

/**
 * The Lead Scoring tab strip (PREC-4055): one numbered tab per step, a green
 * check or a red "!" on the tabs that are not current, and a hover panel that
 * lists a tab's issues and jumps to each one.
 */
export const ScoreTabs: FunctionComponent<ScoreTabsProps> = ({ currentStep }) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { score } = useScoreConfig();
	const issues = useScoreIssues();
	const jobTitlesEnabled = useIsFeatureActive(FeaturesCodes.JOB_TITLE_SCREENING);
	const steps = visibleScoreSteps(!!jobTitlesEnabled);

	const [hovered, setHovered] = useState<{ step: ScoreStep; anchor: HTMLElement } | null>(null);
	const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

	const cancelClose = () => {
		if (closeTimer.current) {
			clearTimeout(closeTimer.current);
			closeTimer.current = null;
		}
	};

	const scheduleClose = () => {
		cancelClose();
		closeTimer.current = setTimeout(() => setHovered(null), PANEL_CLOSE_DELAY_MS);
	};

	const openFor = (step: ScoreStep, anchor: HTMLElement, stepIssues: ScoreIssue[]) => {
		cancelClose();

		if (stepIssues.length > 0) {
			setHovered({ step, anchor });
		}
	};

	const hoveredIssues = hovered ? issuesForStep(issues, hovered.step) : [];

	return (
		<>
			<div className="flex overflow-x-auto border-b border-border-light" role="tablist">
				{steps.map((step) => {
					const isCurrent = step === currentStep;
					const number = scoreStepNumber(step, steps);
					// Until the score has loaded there is nothing to grade: every badge shows its number.
					const status = score ? stepStatus(issues, step) : "none";
					const stepIssues = issuesForStep(issues, step);

					return (
						<button
							key={step}
							aria-selected={isCurrent}
							className={classNames(
								"flex cursor-pointer items-center gap-2 whitespace-nowrap border-b-2 px-4 py-2 text-sm font-medium transition-colors",
								isCurrent
									? "border-brand-light text-brand-light"
									: "border-transparent text-text-meta hover:border-gray-300 hover:text-text"
							)}
							data-testid={`score-tab-${step}`}
							role="tab"
							type="button"
							onClick={() => navigate(scoreStepPath(step))}
							onMouseEnter={(event) => openFor(step, event.currentTarget, stepIssues)}
							onMouseLeave={scheduleClose}
						>
							{number !== null && <Badge isCurrent={isCurrent} number={number} status={status} />}
							{t(`leadScoring.stepper.${step}`)}
						</button>
					);
				})}
			</div>
			{hovered && hoveredIssues.length > 0 && (
				<IssuePanel
					anchorEl={hovered.anchor}
					issues={hoveredIssues}
					onMouseEnter={cancelClose}
					onMouseLeave={scheduleClose}
				/>
			)}
		</>
	);
};
